import PropTypes from 'prop-types';
import { useState, useEffect, useRef, useCallback } from 'react';

// @mui
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Popover from '@mui/material/Popover';
import { alpha } from '@mui/material/styles';
import { appBarClasses } from '@mui/material/AppBar';

// routes
import { usePathname } from 'src/routes/hooks';
import { useActiveLink } from 'src/routes/hooks/use-active-link';

// components
import NavItem from './nav-item';

// ----------------------------------------------------------------------

export default function NavList({ data, depth, hasChild, config }) {
  const navRef = useRef(null);

  const pathname = usePathname();

  const active = useActiveLink(data);

  const externalLink = data.path?.includes('http');

  const [open, setOpen] = useState(false);

  // ----------------------------------------------------------------------
  // Close when route changes
  // ----------------------------------------------------------------------

  useEffect(() => {
    if (open) {
      setOpen(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // ----------------------------------------------------------------------
  // Reset body/AppBar styles
  // ----------------------------------------------------------------------

  useEffect(() => {
    const appBarEl = Array.from(document.querySelectorAll(`.${appBarClasses.root}`));

    const resetStyles = () => {
      document.body.style.overflow = '';
      document.body.style.padding = '';

      appBarEl.forEach((elem) => {
        elem.style.padding = '';
      });
    };

    resetStyles();

    return () => {
      resetStyles();
    };
  }, [open]);

  // ----------------------------------------------------------------------
  // Open
  // ----------------------------------------------------------------------

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  // ----------------------------------------------------------------------
  // Close
  // ----------------------------------------------------------------------

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <>
      <NavItem
        ref={navRef}
        item={data}
        depth={depth}
        open={open}
        active={active}
        externalLink={externalLink}
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
        config={config}
      />

      {hasChild && (
        <Popover
          open={open}
          anchorEl={navRef.current}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: 'left',
          }}
          slotProps={{
            paper: {
              onMouseEnter: handleOpen,
              onMouseLeave: handleClose,

              sx: {
                // ----------------------------------------------------
                // Popup shell
                // ----------------------------------------------------

                width: 300,
                maxWidth: 'calc(100vw - 24px)',

                borderRadius: 2,

                overflow: 'hidden',

                bgcolor: 'background.paper',

                border: (theme) => `1px solid ${alpha(theme.palette.grey[500], 0.14)}`,

                boxShadow: (theme) => `0 12px 32px ${alpha(theme.palette.common.black, 0.12)}`,

                p: 0,

                ...(open && {
                  pointerEvents: 'auto',
                }),
              },
            },
          }}
          sx={{
            pointerEvents: 'none',
          }}
        >
          {/* ==========================================================
              FIXED POPUP TITLE
              ========================================================== */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: 44,
              minHeight: 44,
              px: 1.5,
              py: 0,
              bgcolor: 'background.paper',
              borderBottom: (theme) => `1px solid ${alpha(theme.palette.grey[500], 0.14)}`,
              flexShrink: 0,
              boxSizing: 'border-box',
            }}
          >
            <Box
              sx={{
                width: 1,
                minWidth: 0,

                // fontSize: '0.875rem',
                // fontWeight: 500,
                // lineHeight: 1.2,

                color: 'text.primary',

                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {data.title}
            </Box>
          </Box>

          {/* ==========================================================
              SCROLLABLE MENU
              ========================================================== */}

          <NavSubList data={data.children} depth={depth} config={config} />
        </Popover>
      )}
    </>
  );
}

NavList.propTypes = {
  config: PropTypes.object,
  data: PropTypes.object,
  depth: PropTypes.number,
  hasChild: PropTypes.bool,
};

// ----------------------------------------------------------------------
// Sub navigation
// ----------------------------------------------------------------------

function NavSubList({ data, depth, config }) {
  return (
    <Stack
      spacing={0}
      sx={{
        width: 1,

        maxHeight: 250,

        overflowY: 'auto',
        overflowX: 'hidden',

        py: 0.5,

        // --------------------------------------------------------------
        // Scrollbar
        // --------------------------------------------------------------

        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(100,100,100,.35) transparent',

        '&::-webkit-scrollbar': {
          width: 5,
        },

        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },

        '&::-webkit-scrollbar-thumb': {
          borderRadius: 10,
          backgroundColor: 'rgba(100,100,100,.35)',
        },

        '&::-webkit-scrollbar-thumb:hover': {
          backgroundColor: 'rgba(100,100,100,.55)',
        },

        // --------------------------------------------------------------
        // Child menu items
        // --------------------------------------------------------------

        '& > a': {
          width: '100%',
          minWidth: 0,
        },

        // Reduce excessive vertical spacing inside popup
        '& .MuiListItemButton-root': {
          minHeight: 42,
        },

        // Slightly tighter typography
        '& .MuiListItemText-root': {
          marginTop: 0,
          marginBottom: 0,
        },
      }}
    >
      {data.map((list) => (
        <NavList
          key={list.title + list.path}
          data={list}
          depth={depth + 1}
          hasChild={
            Array.isArray(list.children) ? list.children.length > 0 : Boolean(list.children)
          }
          config={config}
        />
      ))}
    </Stack>
  );
}

NavSubList.propTypes = {
  config: PropTypes.object,
  data: PropTypes.array,
  depth: PropTypes.number,
};
